import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  CircularProgress,
  Button,
} from '@mui/material';
import { Search as SearchIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import TextFile from '@icons/TextFile';
import Colors from '@constants/Colors';
import { useParams, useNavigate } from 'react-router-dom';
import { getALCorrectionData, completeALCorrection } from '@services/dashboardService';
import { showErrorSnackbar, showSuccessSnacbar } from '@components/shared-layouts/Snackbar/helper';

interface FileItem {
  id: string;
  name: string;
  size: string;
}

interface ALCorrectionData {
  message: string;
  instances: {
    [key: string]: {
      sets: {
        hard: Array<{ start: number; end: number }>;
        med: Array<{ start: number; end: number }>;
        easy: Array<{ start: number; end: number }>;
        custom: Array<{ start: number; end: number }>;
      };
    };
  };
  classes: string[];
  keys: any[];
  colors: string[];
}

const ALCorrectionPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState<boolean>(false);

  const { projectId, project_type } = useParams<{ projectId: string; project_type: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchALCorrectionData = async () => {
      if (!projectId) {
        setError('Project ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data: ALCorrectionData = await getALCorrectionData(projectId);
        
        // Extract file names from instances object
        const fileNames = Object.keys(data.instances || {});
        
        // Convert to FileItem format
        const fileItems: FileItem[] = fileNames.map((fileName, index) => ({
          id: `file-${index}`,
          name: fileName,
          size: 'N/A', // Size not available in API response
        }));
        
        setFiles(fileItems);
      } catch (err: any) {
        console.error('Error fetching AL correction data:', err);
        const errorMessage = err?.error?.message || 'Failed to fetch AL correction data';
        setError(errorMessage);
        showErrorSnackbar(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchALCorrectionData();
  }, [projectId]);

  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) {
      return files;
    }
    
    const query = searchQuery.toLowerCase();
    return files.filter((file) =>
      file.name.toLowerCase().includes(query)
    );
  }, [files, searchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleFileClick = (fileName: string) => {
    if (projectId && project_type) {
      const encodedFileName = encodeURIComponent(fileName);
      navigate(`/${project_type}/operations/${projectId}/auto-labelling-correction/${encodedFileName}`);
    }
  };

  const handleComplete = async () => {
    if (!projectId) {
      showErrorSnackbar('Project ID is missing');
      return;
    }

    try {
      setCompleting(true);
      const response = await completeALCorrection(projectId);
      showSuccessSnacbar(response?.data?.message || 'AL correction completed successfully');
    } catch (err: any) {
      console.error('Error completing AL correction:', err);
      const errorMessage = err?.error?.message || 'Failed to complete AL correction';
      showErrorSnackbar(errorMessage);
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          width: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          width: '100%',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: '24px',
            maxWidth: '600px',
            margin: '0 auto',
            backgroundColor: Colors.neutralwhite_50,
          }}
        >
          <Typography variant="h6" sx={{ color: Colors.error_600, textAlign: 'center' }}>
            Error Loading Data
          </Typography>
          <Typography variant="body2" sx={{ color: Colors.neutral_gray_500, marginTop: '8px', textAlign: 'center' }}>
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', padding: { xs: '10px', sm: '14px 25px' }, width: '100%' }}>
      <Paper
        elevation={3}
        sx={{
          padding: '24px',
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          backgroundColor: Colors.neutralwhite_50,
        }}
      >
        <Box sx={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: Colors.neutralblack,
            }}
          >
            Correct Auto-Labelling
          </Typography>
          {files.length > 0 && (
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={handleComplete}
              disabled={completing}
              sx={{
                backgroundColor: Colors.primary,
                '&:hover': {
                  backgroundColor: Colors.primary_600,
                },
              }}
            >
              {completing ? 'Completing...' : 'Complete'}
            </Button>
          )}
        </Box>

        <Box sx={{ marginBottom: '24px' }}>
          <TextField
            fullWidth
            placeholder="Search .nii.gz files by name..."
            value={searchQuery}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: Colors.neutral_gray_400 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: Colors.white,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: Colors.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: Colors.primary,
                },
              },
            }}
          />
          
          <Typography
            variant="body2"
            sx={{
              marginTop: '8px',
              color: Colors.neutral_gray_500,
            }}
          >
            Showing {filteredFiles.length} of {files.length} files
          </Typography>
        </Box>

        {filteredFiles.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              padding: '48px',
              color: Colors.neutral_gray_500,
            }}
          >
            <Typography variant="h6">No files found</Typography>
            <Typography variant="body2" sx={{ marginTop: '8px' }}>
              {files.length === 0 
                ? 'No AL correction data available for this project'
                : 'Try adjusting your search query'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredFiles.map((file) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={file.id}>
                <Card
                  onClick={() => handleFileClick(file.name)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: Colors.Elevation_4,
                    },
                    backgroundColor: Colors.white,
                    border: `1px solid ${Colors.neutral_gray_100}`,
                  }}
                >
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px 16px',
                      flexGrow: 1,
                      '&:last-child': {
                        paddingBottom: '20px',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '12px',
                        padding: '12px',
                        backgroundColor: Colors.neutralwhite_10,
                        borderRadius: '8px',
                        width: '64px',
                        height: '64px',
                      }}
                    >
                      <TextFile color={Colors.primary} />
                    </Box>
                    
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: Colors.neutralblack,
                        textAlign: 'center',
                        marginBottom: '4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                      }}
                      title={file.name}
                    >
                      {file.name}
                    </Typography>
                    
                    <Typography
                      variant="caption"
                      sx={{
                        color: Colors.neutral_gray_400,
                        textAlign: 'center',
                        fontSize: '0.7rem',
                      }}
                    >
                      {file.size}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default ALCorrectionPage;

